import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, CheckCircle2, Crown, Phone } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BRAND_SERVICES = [
  'Roadworthy Inspections', 'Log Book Service', 'European Car Specialist',
  'Aircon Service', 'Battery & Wheel Alignment', 'All Engine, Minor & Major Repairs',
  'Brakes & Clutch Repairs',
];

const EMPTY = {
  company_name: '', first_name: '', last_name: '', mobile: '', email: '',
  license_number: '', abn: '', address: '',
};

export default function MemberSignup() {
  const [params] = useSearchParams();
  const siteId = params.get('site') || null;
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.first_name.trim()) return setError('Please enter your first name');
    if (!form.mobile.trim()) return setError('Please enter your mobile number');
    if (!form.email.trim()) return setError('Please enter your email');
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/public/membership/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          site_id: siteId,
          company_name: form.company_name.trim() || null,
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim() || null,
          mobile: form.mobile.trim(),
          email: form.email.trim(),
          license_number: form.license_number.trim() || null,
          abn: form.abn.trim() || null,
          address: form.address.trim() || null,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Signup failed — please try again');
      setDone(d.message || 'Thanks for signing up!');
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4 py-10" data-testid="member-signup-page">
      <div className="w-full max-w-lg">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 mb-3">
            <Crown className="h-7 w-7 text-emerald-400" />
          </div>
          <p className="text-emerald-400 text-xs font-bold tracking-[0.3em] uppercase mb-1" data-testid="signup-brand">Hoppers Service Centre</p>
          <h1 className="text-white text-2xl sm:text-3xl font-bold font-['Chivo']">Become a Member</h1>
          <p className="text-slate-400 text-sm mt-1">Join our membership program — your monthly subscription builds credit towards every service.</p>
          <p className="text-slate-200 text-sm mt-2 inline-flex items-center gap-1.5 font-semibold" data-testid="signup-phone">
            <Phone className="h-3.5 w-3.5 text-emerald-400" /> Call us · 9749 0244
          </p>
          <div className="flex flex-wrap justify-center gap-1.5 mt-4 max-w-md mx-auto" data-testid="signup-services">
            {BRAND_SERVICES.map((s) => (
              <span key={s} className="text-[10px] uppercase tracking-wide text-slate-300 bg-white/5 border border-white/10 rounded-full px-2.5 py-1">
                {s}
              </span>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8">
          {done ? (
            <div className="text-center py-8" data-testid="signup-success">
              <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-slate-900 mb-2">You're in!</h2>
              <p className="text-slate-600 text-sm">{done}</p>
              <p className="text-slate-400 text-xs mt-4">Our team will be in touch to confirm your membership and set up your member portal login.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label>Company name</Label>
                <Input value={form.company_name} onChange={set('company_name')} placeholder="Your business name" data-testid="signup-company" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>First name *</Label>
                  <Input value={form.first_name} onChange={set('first_name')} data-testid="signup-firstname" />
                </div>
                <div>
                  <Label>Last name</Label>
                  <Input value={form.last_name} onChange={set('last_name')} data-testid="signup-lastname" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Mobile *</Label>
                  <Input value={form.mobile} onChange={set('mobile')} placeholder="04xx xxx xxx" data-testid="signup-mobile" />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input type="email" value={form.email} onChange={set('email')} data-testid="signup-email" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Licence number</Label>
                  <Input value={form.license_number} onChange={set('license_number')} data-testid="signup-license" />
                </div>
                <div>
                  <Label>ABN</Label>
                  <Input value={form.abn} onChange={set('abn')} data-testid="signup-abn" />
                </div>
              </div>
              <div>
                <Label>Address</Label>
                <Input value={form.address} onChange={set('address')} data-testid="signup-address" />
              </div>
              {error && <p className="text-sm text-rose-600" data-testid="signup-error">{error}</p>}
              <Button type="submit" disabled={submitting}
                className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl" data-testid="signup-submit">
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Sign me up
              </Button>
              <p className="text-[11px] text-slate-400 text-center">By signing up you agree to be contacted about your membership.</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
